import { useEffect, useState } from 'react';
import { getAdminEditRequests, processEditRequest, GetAdminEditRequestsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, User } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

type EditRequest = GetAdminEditRequestsOutputType['requests'][0];

const timeAgo = (d?: string) => { try { return d ? formatDistanceToNow(parseISO(d), { addSuffix: true }) : '—'; } catch { return '—'; } };

const SENSITIVE = ['Bank Account Holder', 'Bank Name', 'Sort Code', 'Account Number'];

export default function EditRequestsAdminTab() {
  const [requests, setRequests] = useState<EditRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [actionDialog, setActionDialog] = useState<{ request: EditRequest; action: 'approve' | 'decline' } | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminEditRequests({}).then(d => setRequests(d.requests)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAction = (req: EditRequest, action: 'approve' | 'decline') => {
    setActionDialog({ request: req, action });
    setAdminNotes('');
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    setProcessing(true);
    try {
      await processEditRequest({
        requestId: actionDialog.request.id,
        action: actionDialog.action,
        adminNotes: adminNotes || undefined,
      });
      toast.success(`Request ${actionDialog.action}d`);
      setActionDialog(null);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setProcessing(false); }
  };

  const counts = { Pending: 0, Approved: 0, Declined: 0 };
  for (const r of requests) { if (r.status in counts) counts[r.status as keyof typeof counts]++; }

  const filtered = requests.filter(r => r.status === filter);

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {(['Pending', 'Approved', 'Declined'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          >
            {s}
            {counts[s] > 0 && (
              <span className={`px-1.5 rounded-full text-xs ${filter === s ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-background text-foreground'}`}>{counts[s]}</span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <User className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No {filter.toLowerCase()} edit requests</p>
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Field</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Change</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Requested</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(req => {
                const isSensitive = SENSITIVE.includes(req.fieldName || '');
                return (
                  <tr key={req.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground text-sm">{req.employeeName}</p>
                      {req.employeeEmail && <p className="text-xs text-muted-foreground">{req.employeeEmail}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-foreground">{req.fieldName}</span>
                        {isSensitive && <span className="text-xs px-1.5 py-0.5 bg-amber-500/10 text-amber-500 rounded-sm">Sensitive</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-xs space-y-0.5">
                        {req.oldValue && <p className="text-muted-foreground line-through">{isSensitive && req.fieldName?.includes('Account') ? '••••' + req.oldValue.slice(-4) : req.oldValue}</p>}
                        <p className="text-foreground font-medium">{isSensitive && req.fieldName?.includes('Account') ? '→ ••••' + req.newValue?.slice(-4) : req.newValue}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell">{timeAgo(req.requestedAt)}</td>
                    <td className="px-4 py-3">
                      {filter === 'Pending' && (
                        <div className="flex gap-1">
                          <Button size="sm" className="bg-primary text-primary-foreground h-7 px-2 text-xs" onClick={() => openAction(req, 'approve')}>
                            <CheckCircle2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => openAction(req, 'decline')}>
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      {filter !== 'Pending' && (
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${req.status === 'Approved' ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>{req.status}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{actionDialog?.action === 'approve' ? 'Approve' : 'Decline'} change to {actionDialog?.request.fieldName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {actionDialog?.request.newValue && (
              <div className="bg-muted/50 rounded p-3 text-xs">
                <p className="text-muted-foreground mb-1">Requested change:</p>
                <p className="text-foreground font-medium">{actionDialog.request.newValue}</p>
                {actionDialog.action === 'approve' && SENSITIVE.includes(actionDialog.request.fieldName || '') && (
                  <p className="text-amber-500 mt-2">⚠ This is a sensitive field. Ensure you have verified this change with the employee.</p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {actionDialog?.action === 'decline' ? 'Reason *' : 'Notes (optional)'}
              </label>
              <Textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} className="min-h-16" placeholder={actionDialog?.action === 'decline' ? 'Explain why...' : 'Optional notes...'} />
            </div>
            <Button onClick={handleAction} disabled={processing || (actionDialog?.action === 'decline' && !adminNotes.trim())} className="w-full bg-primary text-primary-foreground">
              {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : actionDialog?.action === 'approve' ? 'Approve & Update Record' : 'Decline Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
